import { trackUserBaseSelector, UserBase } from '@fullstory/analytics';
import { DXObjectViewedBase } from '@fullstory/analytics/types';
import { AuditMetadata } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

export const FROM_CONTEXT_REPLAY_QUERY_PARAM = 'fromReplay';
export const FROM_CONTEXT_DASHBOARD_QUERY_PARAM = 'dashboard';
export const FROM_CONTEXT_TEMPLATE_QUERY_PARAM = 'dashboardTemplate';

type ViewedData = UserBase & DXObjectViewedBase;

const getFromContext = (
  location: ReturnType<typeof useLocation>,
): DXObjectViewedBase['from_context_str'] | undefined => {
  const params = new URLSearchParams(location.search);

  if (params.get(FROM_CONTEXT_REPLAY_QUERY_PARAM)) return 'replay';
  if (params.get(FROM_CONTEXT_DASHBOARD_QUERY_PARAM)) return 'dashboard';
  if (params.get(FROM_CONTEXT_TEMPLATE_QUERY_PARAM)) return 'dashboardTemplate';

  return undefined;
};

const isDefaultObject = (savedId: string | null | undefined, hash: string | null | undefined) =>
  !savedId && !hash;

interface Params {
  /**
   * Used for some metadata and to determine when the analytics event is ready to fire.
   *
   * It is expected that the id and hash will always align with their objects.
   */
  data: {
    savedId: string | null | undefined;
    savedRecord: { metadata: AuditMetadata | null } | null | undefined;
    hash: string | null | undefined;
    unnamedRecord: object | null | undefined;
  };
  /**
   * Function invoked to call the appropriate Viewed analytics event.
   *
   * The viewed data is a common set of DX object data that should be merged in.
   */
  trackFn: (data: ViewedData) => void;
}

/**
 * Helper hook for emitting DX Object Viewed analytics events.
 *
 * A DX Object is considered Viewed at the point of its applicable data being loaded.
 * Only one Viewed event will be invoked via callback.
 * A default object will not be considered Viewed
 * (eg Someone clicks new Metric and does nothing with the form is not a Viewed event).
 *
 * Standardizes some analytics properties and wraps up the same stateful logic.
 */
export const useDXObjectViewedAnalyticsEvent = ({ data, trackFn }: Params) => {
  const { savedId, savedRecord, hash, unnamedRecord } = data;

  // use refs to avoid unneccessary re-renders
  const triggered = useRef(false);
  const triggeredSavedId = useRef<string | undefined>();
  // dont trigger viewed events on default objects
  const triggerEnabled = useRef(!isDefaultObject(savedId, hash));
  const userAnalytics = useSelector(trackUserBaseSelector);
  const location = useLocation();

  // track the event as soon as all the available data is loaded
  const ready = (!!savedId && !!savedRecord) || (!!hash && !!unnamedRecord);

  // reset state when the saved object changes
  // for example, the user clicked on a different metric link while already on the metric detail page
  // this would represent a navigation to a new metric, even if the consuming component did not re-mount.
  useEffect(() => {
    // consider both falsy no change
    if ((triggeredSavedId.current || savedId) && triggeredSavedId.current !== savedId) {
      triggered.current = false;
      triggerEnabled.current = !isDefaultObject(savedId, hash);
    }
  }, [savedId, hash]);

  // trigger when ready for the first time
  useEffect(() => {
    if (ready && !triggered.current && triggerEnabled.current) {
      trackFn({
        ...userAnalytics,
        from_context_str: getFromContext(location),
        current_owner_bool: userAnalytics.user_email_str === savedRecord?.metadata?.createdBy,
        is_unnamed_bool: !!hash,
      });

      triggered.current = true;
      if (savedId) {
        triggeredSavedId.current = savedId;
      }
    }
  }, [userAnalytics, hash, location, ready, trackFn, savedRecord?.metadata?.createdBy, savedId]);

  // after ready for the first time, close the window immediately to avoid
  // triggering the event event multiple times
  useEffect(() => {
    if (ready) {
      triggerEnabled.current = false;
    }
  }, [ready]);
};
